import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Upload, Download, Database } from 'lucide-react';

export function ImportExport() {
    const [accounts, setAccounts] = useState([]);
    const [file, setFile] = useState(null);
    const [source, setSource] = useState('revolut');
    const [accountId, setAccountId] = useState('');
    const [preview, setPreview] = useState(null);
    const [importing, setImporting] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        api.getAccounts().then((data) => {
            setAccounts(data);
            if (data[0]) setAccountId(data[0].id);
        });
    }, []);

    const handleUpload = (e) => {
        e.preventDefault();
        if (!file || !accountId) return;

        setImporting(true);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('source', source);
        formData.append('account_id', accountId);

        api.uploadImport(formData)
            .then((data) => {
                setPreview(data);
                setMessage(data.rows.length > 0 ? 'File analizzato. Controlla l\'anteprima e conferma.' : 'Nessuna riga valida trovata.');
            })
            .catch((err) => setMessage(err.response?.data?.message || 'Errore durante l\'analisi.'))
            .finally(() => setImporting(false));
    };

    const handleConfirm = () => {
        if (!preview) return;
        api.confirmImport(preview.import.id, { rows: preview.rows, account_id: accountId })
            .then((data) => {
                setMessage(data.message);
                setPreview(null);
                setFile(null);
            })
            .catch((err) => setMessage(err.response?.data?.message || 'Errore durante l\'importazione.'));
    };

    const handleExport = () => {
        api.exportTransactions().then((res) => {
            const blob = new Blob([res.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'tascio_movimenti.csv';
            a.click();
            window.URL.revokeObjectURL(url);
        });
    };

    const handleBackup = () => {
        setMessage('Creazione backup in corso...');
        api.createBackup()
            .then((res) => {
                const blob = new Blob([res.data], { type: 'application/sql' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'tascio_backup.sql';
                a.click();
                window.URL.revokeObjectURL(url);
                setMessage('Backup scaricato.');
            })
            .catch((err) => {
                setMessage(err.response?.data?.message || 'Errore durante il backup.');
            });
    };

    return (
        <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-6">Importa / Esporta</h1>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
                            <Upload size={20} />
                        </div>
                        <h2 className="font-semibold text-gray-900">Importa CSV</h2>
                    </div>

                    <form onSubmit={handleUpload} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Formato</label>
                            <select
                                value={source}
                                onChange={(e) => setSource(e.target.value)}
                                className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-indigo-500 outline-none"
                            >
                                <option value="revolut">Revolut</option>
                                <option value="postepay">Postepay</option>
                                <option value="manual">Manuale / Generico</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Conto di destinazione</label>
                            <select
                                value={accountId}
                                onChange={(e) => setAccountId(e.target.value)}
                                className="w-full px-3 py-2 rounded-xl border border-gray-200 focus:border-indigo-500 outline-none"
                                required
                            >
                                <option value="">Seleziona</option>
                                {accounts.map((a) => (
                                    <option key={a.id} value={a.id}>{a.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">File CSV</label>
                            <input
                                type="file"
                                accept=".csv,.txt"
                                onChange={(e) => setFile(e.target.files[0])}
                                className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                                required
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={importing}
                            className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-medium transition disabled:opacity-60"
                        >
                            {importing ? 'Analisi...' : 'Analizza file'}
                        </button>
                    </form>
                </div>

                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600">
                            <Download size={20} />
                        </div>
                        <h2 className="font-semibold text-gray-900">Esporta movimenti</h2>
                    </div>
                    <p className="text-sm text-gray-500 mb-4">Scarica tutti i movimenti in formato CSV.</p>
                    <button
                        onClick={handleExport}
                        className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-medium transition"
                    >
                        Scarica CSV
                    </button>
                </div>

                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center text-amber-600">
                            <Database size={20} />
                        </div>
                        <h2 className="font-semibold text-gray-900">Backup database</h2>
                    </div>
                    <p className="text-sm text-gray-500 mb-4">Scarica un backup completo del database MySQL.</p>
                    <button
                        onClick={handleBackup}
                        className="w-full py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-sm font-medium transition"
                    >
                        Scarica backup
                    </button>
                </div>
            </div>

            {message && (
                <div className="mt-6 p-4 bg-blue-50 text-blue-700 rounded-xl text-sm">{message}</div>
            )}

            {preview && preview.rows.length > 0 && (
                <div className="mt-6">
                    <h3 className="font-semibold text-gray-900 mb-3">Anteprima ({preview.rows.length} righe)</h3>
                    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden max-h-96 overflow-y-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                    <th className="text-left px-4 py-2 font-medium text-gray-500">Data</th>
                                    <th className="text-left px-4 py-2 font-medium text-gray-500">Descrizione</th>
                                    <th className="text-right px-4 py-2 font-medium text-gray-500">Importo</th>
                                    <th className="text-left px-4 py-2 font-medium text-gray-500">Tipo</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {preview.rows.map((row, idx) => (
                                    <tr key={idx}>
                                        <td className="px-4 py-2">{row.transaction_date}</td>
                                        <td className="px-4 py-2">{row.description}</td>
                                        <td className="px-4 py-2 text-right">€ {parseFloat(row.amount).toFixed(2)}</td>
                                        <td className="px-4 py-2 capitalize">{row.type === 'income' ? 'Entrata' : 'Spesa'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <button
                        onClick={handleConfirm}
                        className="mt-4 px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-medium transition"
                    >
                        Conferma importazione
                    </button>
                </div>
            )}

            {preview && preview.errors.length > 0 && (
                <div className="mt-6 p-4 bg-red-50 text-red-700 rounded-xl text-sm">
                    <p className="font-semibold mb-2">Errori ({preview.errors.length}):</p>
                    <ul className="list-disc pl-5 space-y-1">
                        {preview.errors.slice(0, 10).map((err, idx) => (
                            <li key={idx}>Riga {err.line}: {err.reason}</li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
